import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { translate, useSmartTranslation } from '../../../cls/TranslationContext'

import {
	Space,
	ConfigProvider,
	Form,
	Input,
	Card,
	Select,
	Row,
	Col,
	Tag,
	Tooltip,
	Button,
	InputNumber
} from 'antd'
import {
	WarningOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'

import Title from 'antd/es/typography/Title'
import { runInAction, toJS } from 'mobx'
import dayjs from 'dayjs'
import { isMobile } from 'react-device-detect'

import { IS_NEW } from '../../../config'
import {
	actLoadSprint,
	actSaveSprint,
	requireSprintById
} from '../../../stores/sprints/actions'
import { useStores } from '../../../cls/stores/MobXProvider'

import {
	RecurrenceFormItems,
	CommentsInput,
	SubtasksInput,
	SubmitButton
} from '../../../components/Form'
import {
	Features,
	FormatedTimestamp,
	UserString
} from '../../../components/Shared'
import ProgressInput from '../../../components/Form/ProgressInput'
import AllowedCategories from '../../../components/Shared/AllowedCategories'
import AssignPlatformsInput from '../../../components/Form/AssignPlatformsInput'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import '../../../design/styles/patterns.css' // Create a CSS file to handle custom styles
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import ProgressModal from '../../../components/Form/ProgressModal'
import { sendNotification } from '../../../stores/notifications/action'
import { Comments } from '../../../cls/utils/comments'
import { formatString, nextSortId } from '../../../config/utils'
import { actLoadUsers } from '../../../stores/ui-lst-users/actions'
import ImportExport from '../../../components/Shared/ImportExport'

const SprintEdit = ({ viewOnly = false }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { itmId } = useParams()
	const {
		uiLstSprints,
		users,
		tourStore,
		priorityOptions,
		appType,
		uiLstUsers
	} = useStores()
	const { appIsClient, appIsTeam } = appType
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const _itm = requireSprintById(itmId)

	const selfPermission =
		_itm.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin

	// If user has no permission to edit, set viewOnly to true
	if (!selfPermission && _itm.loaded !== IS_NEW) {
		viewOnly = true
	}

	/**
	 * Generates initial values for recurrence-related form fields based on the sprint item.
	 * @param {object} _itm - The sprint item object.
	 * @returns {object} - The initial values for recurrence form inputs.
	 */
	const recurrenceInitialValues = (_itm) => ({
		recurrenceNext: _itm.recurrence.next
			? dayjs.unix(_itm.recurrence.next)
			: null,
		recurrenceType: _itm.recurrence.type,
		recurrenceStartEnd: _itm.recurrence.startEnd?.map((date) =>
			dayjs.unix(date)
		),
		recurrenceTimes: _itm.recurrence.times?.map((time) =>
			dayjs(time, 'HH:mm')
		),
		recurrenceDaysOfWeek: _itm.recurrence.daysOfWeek,
		recurrenceDayOfMonth: _itm.recurrence.dayOfMonth,
		recurrenceMonths: _itm.recurrence.months
	})

	/**
	 * Generates the initial values for the sprint form fields based on the sprint item.
	 * @returns {object} - The default form values.
	 */
	const generateInitialFormValues = () => ({
		// user_id: _itm.createdBy || loggedInUserId,
		createdBy: _itm.createdBy || loggedInUserId,
		for_user_id: _itm.for_user_id || loggedInUserId,
		category_id: _itm.category_id,
		name: _itm.name,
		progress: _itm.progress,

		// Generate initial values for recurrence fields
		...recurrenceInitialValues(_itm),

		subtasks: toJS(_itm.subtasks.list),
		comments: toJS(_itm.comments.list),
		platformIds: _itm.platformIds,
		priority: _itm.priority || 0,
		effortEstimate: _itm.effortEstimate || 0
	})

	// Load users
	/**
	 * Loads all users when the component is mounted or when the logged-in user ID changes.
	 */
	useEffect(() => {
		actLoadUsers({ show_all: true }).catch(console.error)
	}, [loggedInUserId])

	// Determine the notification type
	const notificationKind = _itm.loaded === IS_NEW ? 'new' : 'update'

	// Determine th table entity for notification based on the app Type
	let tableEntity = appIsClient ? 'sprints' : appIsTeam ? 'sprints_team' : ''

	/**
	 * Updates the forUser state when the sprint item changes.
	 */
	const [forUser, setForUser] = useState({})
	useEffect(() => {
		setForUser(users.requireUserById(_itm.for_user_id))
	}, [_itm])

	/**
	 * Handles saving the sprint form data by updating the sprint item state and triggering persistence actions.
	 * @param {object} vals - The form values submitted by the user.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				// Set creator and timestamp
				!_itm.createdBy && _itm.setCreator(loggedInUserId)

				// Reset last full load
				_itm.loaded === IS_NEW && _itm.resetLastFullLoad()

				// Form values
				// _itm.setUser_id(vals.user_id)
				_itm.setForUserId(vals.for_user_id)
				_itm.setPlatformIds(vals.platformIds)
				_itm.setCategory_id(vals?.category_id)
				_itm.setName(vals?.name)
				_itm.setProgress(vals.progress)
				isSuperAdmin && _itm.setCreatedBy(vals.createdBy)
				_itm.setPriority(vals.priority)
				_itm.setEffortEstimate(vals.effortEstimate)

				// Recurrence
				_itm.recurrence.setType(vals.recurrenceType)
				_itm.recurrence.setStartEnd(
					// save as array of timestamps
					vals.recurrenceStartEnd &&
						vals.recurrenceStartEnd.map((date) => date.unix())
				)
				_itm.recurrence.setTimes(
					vals.recurrenceTimes &&
						vals.recurrenceTimes.map((time) => time.format('HH:mm'))
				)
				_itm.recurrence.setDaysOfWeek(vals.recurrenceDaysOfWeek || [])
				_itm.recurrence.setDayOfMonth(vals.recurrenceDayOfMonth || '')
				_itm.recurrence.setMonths(vals.recurrenceMonths || [])

				if (
					vals.recurrenceType !== '' &&
					!_itm.recurrence.next &&
					_itm.progress !== 'completed'
				) {
					_itm.setRecurrenceNext()
				}
				// Final recurrence save
				_itm.chgInFull(['recurrence'], toJS(_itm.recurrence))

				// Subtasks
				_itm.setSubtasks(vals.subtasks)
				// Comments
				_itm.setComments(vals.comments)
			})
			// console.warn('recurrence', toJS(_itm.full.recurrence))
			actSaveSprint(itmId, toJS(_itm.full))
				.then(() => {
					// Create notification only if createdBy is not equal to for_user_id
					if (_itm.createdBy != _itm?.for_user_id) {
						sendNotification(
							loggedInUser,
							forUser?.email,
							_itm?.for_user_id,
							tableEntity,
							itmId,
							notificationKind
						)
					}
					navigate('/sprints')
				})
				.catch(console.error)
		}
	}

	const [form] = Form.useForm()

	/**
	 * Synchronizes the form fields whenever the sprint item is modified.
	 */
	useEffect(() => {
		form.setFieldsValue(generateInitialFormValues())
	}, [_itm.modified]) // Track _itm.modified

	const [formIsDisabled, setFormIsDisabled] = React.useState(
		!!_itm.to_delete ?? viewOnly ? true : !['draft'].includes(_itm.progress)
	)

	// Track changes to _itm.to_delete to update form disabled state and tag visibility
	useEffect(() => {
		setFormIsDisabled(
			viewOnly
				? true
				: !!_itm.to_delete ?? !['draft'].includes(_itm.progress)
		)
		form.setFieldsValue(generateInitialFormValues())
	}, [_itm.to_delete, viewOnly])

	const userIdTrack = Form.useWatch('for_user_id', form)

	const [features, setFeatures] = React.useState({
		recurrence: _itm.recurrence.type !== '',
		subtasks: toJS(_itm.subtasks.list)?.length > 0,
		comments: viewOnly ? true : toJS(_itm.comments.list)?.length > 0
	})

	/**
	 * Automatically sets default progress to 'active' if the sprint is new and the user is an admin or super admin.
	 */
	useEffect(() => {
		if (_itm.loaded == IS_NEW && (isAdmin || isSuperAdmin)) {
			form.setFieldsValue({
				progress: 'active'
			})
		}
	}, [isAdmin, isSuperAdmin])

	/**
	 * Clears specific form fields when corresponding features (recurrence, subtasks, comments) are disabled.
	 * @param {object} features - The state object indicating active features.
	 */
	const onFeaturesChange = (features) => {
		if (!features.recurrence) {
			form.setFieldsValue({
				recurrenceType: '',
				recurrenceStartEnd: [],
				recurrenceMonthsTrack: [],
				recurrenceDaysOfWeek: [],
				recurrenceDayOfMonth: '',
				recurrenceMonths: [],
				recurrenceTimes: []
			})
		}
		if (!features.subtasks) {
			form.setFieldsValue({
				subtasks: []
			})
		}
		if (!features.comments) {
			form.setFieldsValue({
				comments: []
			})
		}
	}

	/**
	 * Opens the modal for setting sprint progress.
	 */
	const onSetProgressChange = () => {
		runInAction(() => {
			uiLstSprints.setModal('sprint-progress', {
				visible: true,
				itmId: itmId
			})
		})
	}

	/**
	 * Parses the current URL and returns query parameters.
	 * @param {string} search - The URL search string.
	 * @returns {URLSearchParams} - The parsed URLSearchParams object.
	 */
	const getQueryParams = (search) => {
		return new URLSearchParams(search)
	}

	// Get query parameters from the current URL
	const queryParams = getQueryParams(window.location.search)
	const deadline = queryParams.get('deadline')
	const sprintName = queryParams.get('sprint_name')
	const sprintDesc = queryParams.get('sprint_desc')
	const sprintImages = JSON.parse(queryParams.get('sprint_images') || '[]')
	const sprintRelatedLink = queryParams.get('sprint_related_link')
	const parentSprintId = queryParams.get('parent_sprint_id')

	/**
	 * Applies default recurrence settings to the form when a valid deadline is provided via URL query and the sprint is new.
	 */
	useEffect(() => {
		// Check if the deadline is valid and if the item is newly loaded
		const isValidDeadline = deadline && dayjs.unix(deadline).isValid()
		const isNewItemLoaded = _itm.loaded === IS_NEW

		if (isValidDeadline && isNewItemLoaded) {
			// Update features to indicate recurrence is enabled
			setFeatures((prevFeatures) => ({
				...prevFeatures,
				recurrence: true
			}))

			// Set the form values with specific recurrence settings
			form.setFieldsValue({
				recurrenceType: 'deadline',
				recurrenceStartEnd: [dayjs.unix(deadline)]
			})
		}
	}, [_itm.loaded])

	// Effect to handle updates based on the sprints query parameter (e.g. name, desc, images)
	useEffect(() => {
		const isNewItemLoaded = _itm.loaded === IS_NEW

		// Proceed if the item is new and the sprint name and parent sprint id is available
		if (isNewItemLoaded && sprintName && parentSprintId) {
			// Initialize form values with the sprint name
			let regeneratedFormValues = {
				name: sprintName
			}

			// Load parent sprint detail and update regeneratedFormValues
			const parentSprintDetails = requireSprintById(parentSprintId)
			if (parentSprintDetails) {
				regeneratedFormValues = {
					...regeneratedFormValues,
					...{
						progress: 'active',
						priority: parentSprintDetails.priority,
						for_user_id: parentSprintDetails.for_user_id,
						category_id: parentSprintDetails.category_id,
						platformIds: parentSprintDetails.platformIds
					}
				}
			}

			// set recurrence details to form values and turn on the feature, if parent sprint has
			if (parentSprintDetails?.recurrence?.type) {
				// Update features to indicate comments is enabled
				setFeatures((prevFeatures) => ({
					...prevFeatures,
					recurrence: true
				}))

				form.setFieldsValue({
					...recurrenceInitialValues(parentSprintDetails)
				})
			}

			if (sprintDesc) {
				// Update features to indicate comments is enabled
				setFeatures((prevFeatures) => ({
					...prevFeatures,
					comments: true
				}))

				// Add comments to the form values
				regeneratedFormValues = {
					...regeneratedFormValues,
					comments:
						new Comments([
							{
								description: decodeURIComponent(sprintDesc),
								images: sprintImages || [],
								web: sprintRelatedLink,
								createdBy: loggedInUserId,
								sortId: nextSortId()
							}
						])?.list || []
				}
			}

			// Set the form values with specific sprint details
			form.setFieldsValue({
				...regeneratedFormValues
			})
		}
	}, [_itm.loaded])

	/*
	 * Tour Reccurence
	 * This is a tour for the recurrence fields. Manually added to the tour due to possible scenario were list item might not have all recurrence types.
	 */
	let tourRecurrence = features.recurrence
		? getTourArrayByKeys('recurrence', ['section'])
		: []

	/*
	 * Tour features
	 * This is a tour for the features of the sprint.
	 */
	const tourFeatures =
		!viewOnly && !formIsDisabled
			? getTourArrayByKeys('features', ['all'])
			: []

	const tourForm = getTourArrayByKeys('form', [
		'section',
		'name',
		'category_id',
		'user_id',
		'for_user_id',
		'platformIds',
		'progress',
		'priority',
		'effortEstimate'
	])

	/**
	 * Start tour:
	 * This function starts the tour for the sprint form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		tourStore.startTour('SprintEdit-tour', [
			// Features
			...tourFeatures,

			// General info
			...getTourArrayByKeys('info', ['name', 'modified']),

			// General form
			...getTourArrayByKeys('form', ['section']),

			// Recurrence
			...tourRecurrence
		])
	}

	/**
	 * Starts the interactive tour focused only on the sprint form fields.
	 */
	const startTourForm = () => {
		tourStore.startTour('SprintEdit-tour', [
			// General form
			...tourForm
		])
	}

	/*
	 * This method executes after the import is done
	 * Should be used to activate sections or fields based on the imported values
	 * exemple if we have values.comments we should activate the comments section
	 */
	const _withValuesExecute = (values) => {
		Object.keys(features).forEach((feature) => {
			if (values[feature]) {
				document
					.getElementById('target-features-' + feature)
					.querySelector('input')
					.click()
			}
		})
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_sprint') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-on')}
								<FormatedTimestamp
									timestamp={_itm.toDeleteTs}
								/>
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			{!viewOnly && !formIsDisabled && (
				<Features
					features={features}
					setFeatures={(val) => {
						setFeatures(val)
						onFeaturesChange(val)
					}}
				/>
			)}
			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('__view%_sprint')}>
								<EyeOutlined
									className="no-print"
									id="target-mode"
								/>{' '}
								{_itm?.name}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create-new%_sprint')
						) : (
							<Tooltip title={t('__edit%')}>
								<SettingOutlined /> {_itm?.name}
							</Tooltip>
						)}{' '}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ProgressModal
					type="sprint"
					modalKey="sprint-progress"
					uiStore={uiLstSprints}
					requireItemById={requireSprintById}
					loadItm={(id) => actLoadSprint(id)}
					saveItem={(itemId, itemObject) => {
						actSaveSprint(itemId, itemObject).then((res) =>
							console.log('Progress saved', res)
						)

						form.setFieldsValue({
							progress: _itm.progress
						})

						_itm.subtasks.list.length > 0 &&
							form.setFieldsValue({
								subtasks: toJS(_itm.subtasks.list)
							})
						// Create notification only if createdBy is not equal to for_user_id
						if (_itm.createdBy != _itm?.for_user_id) {
							sendNotification(
								loggedInUser,
								forUser?.email,
								_itm?.for_user_id,
								tableEntity,
								itmId,
								t('single.progress')
							)
						}
					}}
				/>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="sprint"
						initialValues={generateInitialFormValues()}
						onFinish={_doSave}
						className={viewOnly && 'view-only-styles'}
					>
						<Row
							gutter={[50, 50]}
							style={
								features.subtasks && {
									borderBottom: '1px solid #f0f0f0'
								}
							}
						>
							<Col
								id="target-form"
								className="section-pattern-dots"
								span={features.recurrence ? 12 : 24}
								style={{
									// backgroundColor: 'grey',
									...(features.recurrence
										? {
												borderRight:
													'1px solid #f0f0f0',
												paddingRight: '25px'
										  }
										: {})
								}}
							>
								<Space
									style={{ width: '100%' }}
									direction="vertical"
								>
									<GeneralTourTitle
										align="end"
										titleProps={{
											text: 'General',
											h: Title,
											level: 5
										}}
										buttonProps={{
											onClick: startTourForm,
											disabled: false,
											style: { marginBottom: 4 }
										}}
									/>
									<div id="target-form-name">
										<Form.Item
											label={t('single.name')}
											tooltip={{
												title: t(
													'single.search.description'
												),
												icon: <InfoCircleOutlined />
											}}
											name="name"
											rules={[
												{
													required: true,
													message: t(
														'__provide.please%single.name'
													)
												}
											]}
										>
											<Input
												maxLength={100}
												showCount={!viewOnly}
												disabled={formIsDisabled}
											/>
										</Form.Item>
									</div>
									<Row
										direction={
											isMobile ? 'vertical' : undefined
										}
										style={{ width: '100%' }}
										gutter={20}
									>
										<Col id="target-form-category_id">
											<Form.Item
												label={t('single.category')}
												tooltip={{
													title: t('single.category'),
													icon: <InfoCircleOutlined />
												}}
												name="category_id"
												rules={[
													{
														required: true,
														message: formatString(
															t(
																'__select.please%single.category'
															)
														)
													}
												]}
											>
												<AllowedCategories
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										{isSuperAdmin && (
											<Col id="target-form-user_id">
												<Form.Item
													label={t(
														'single.created-by.label'
													)}
													tooltip={{
														title: t(
															'single.created-by.description'
														),
														icon: (
															<InfoCircleOutlined />
														)
													}}
													name="createdBy"
													rules={[
														{
															required: true,
															message: t(
																'__select.please%_user'
															)
														}
													]}
												>
													<Select
														disabled={
															formIsDisabled
														}
														placeholder={t(
															'__select.item%_user'
														)}
														allowClear
														style={{
															minWidth: '200px'
														}}
														options={[
															...new Set([
																...[
																	loggedInUserId
																],
																...uiLstUsers.ids
															])
														].map((createdBy) => {
															return {
																label: (
																	<UserString
																		key={
																			createdBy
																		}
																		userId={
																			createdBy
																		}
																		path={[
																			'name'
																		]}
																	/>
																),
																value: createdBy
															}
														})}
													/>
												</Form.Item>
											</Col>
										)}
										<Col id="target-form-for_user_id">
											<Form.Item
												label={t(
													'single.for-user.label'
												)}
												tooltip={{
													title: t(
														'single.for-user.description'
													),
													icon: <InfoCircleOutlined />
												}}
												name="for_user_id"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%_user'
														)
													}
												]}
											>
												<Select
													disabled={formIsDisabled}
													placeholder={t(
														'__select.item%_user'
													)}
													allowClear
													style={{
														minWidth: '200px'
													}}
													options={[
														...new Set([
															...[loggedInUserId],
															...uiLstUsers.ids
														])
													].map((createdBy) => {
														return {
															label: (
																<UserString
																	key={
																		createdBy
																	}
																	userId={
																		createdBy
																	}
																	path={[
																		'name'
																	]}
																/>
															),
															value: createdBy
														}
													})}
												/>
											</Form.Item>
										</Col>
									</Row>
									<div id="target-form-platformIds">
										{userIdTrack && (
											<Form.Item
												label={t('single.platform')}
												tooltip={{
													title: t(
														'single.platform-instructions'
													),
													icon: <InfoCircleOutlined />
												}}
												name="platformIds"
											>
												<AssignPlatformsInput
													user_id={userIdTrack}
													disabled={formIsDisabled}
												/>
											</Form.Item>
										)}
									</div>

									<Row align="bottom" gutter={6}>
										<Col id="target-form-progress">
											<Row align="bottom">
												<Col>
													<Form.Item
														label={t(
															'single.progress'
														)}
														tooltip={{
															title: t(
																'single.curent-progress'
															),
															icon: (
																<InfoCircleOutlined />
															)
														}}
														name="progress"
														rules={[
															{
																required: true,
																message: t(
																	'single.progress-required'
																)
															}
														]}
													>
														<ProgressInput
															viewOnly={viewOnly}
															disabled={
																formIsDisabled ||
																_itm.loaded !==
																	IS_NEW
															}
														/>
													</Form.Item>
												</Col>
												<Col>
													{viewOnly &&
														(selfPermission ||
															users.canUser(
																'write',
																tableEntity
															)) && (
															<Col>
																<Form.Item>
																	<Button
																		className="no-print"
																		type="primary"
																		onClick={(
																			e
																		) => {
																			e.preventDefault()
																			onSetProgressChange()
																		}}
																		ghost
																	>
																		{t(
																			'single.set-progress'
																		)}
																	</Button>
																</Form.Item>
															</Col>
														)}
												</Col>
											</Row>
										</Col>

										<Col id="target-form-priority">
											<Form.Item
												label={t(
													'single.filter.priority-desc'
												)}
												tooltip={{
													title: t(
														'single.priority-of-sprint'
													),
													icon: <InfoCircleOutlined />
												}}
												name="priority"
												rules={[
													{
														required: true,
														message: t(
															'single.priority-required'
														)
													}
												]}
											>
												<Select
													disabled={formIsDisabled}
													placeholder={t(
														'single.priority-select'
													)}
													allowClear
													style={{
														minWidth: '200px'
													}}
													options={priorityOptions}
												/>
											</Form.Item>
										</Col>

										<Col id="target-form-effortEstimate">
											<Form.Item
												label={t(
													'single.effort.estimate'
												)}
												tooltip={{
													title: t(
														'single.effort.estimate-description'
													),
													icon: <InfoCircleOutlined />
												}}
												name="effortEstimate"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.effort.estimate'
														)
													}
												]}
											>
												<InputNumber
													addonBefore={t(
														'_hour.multiple'
													)}
													min={0}
													max={24}
													step={1}
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Space>
							</Col>
							{features.recurrence && (
								<Col
									span={12}
									className="print-only-width-full"
								>
									<RecurrenceFormItems
										form={form}
										disabled={formIsDisabled}
									/>
								</Col>
							)}
						</Row>
						<Row
							gutter={[50, 50]}
							style={
								!viewOnly && {
									borderBottom: '1px solid #f0f0f0'
								}
							}
						>
							{features.subtasks && (
								<Col
									className="print-only-width-full"
									span={
										features.comments && !isMobile ? 12 : 24
									}
									style={
										features.comments && {
											borderRight: '1px solid #f0f0f0',
											paddingRight: '25px'
										}
									}
								>
									<Title level={5}>
										{translate('_subtask.multiple')}
									</Title>
									<Form.Item
										label={t('__items%_subtask.multiple')}
										name="subtasks"
										tooltip={{
											title: t(
												'__x-can-also-have-x%_subtask.multiple%_comment.multiple'
											),
											icon: <InfoCircleOutlined />
										}}
									>
										<SubtasksInput
											displaySubtaskDescription
											createdBy={loggedInUserId}
											disabled={formIsDisabled}
										/>
									</Form.Item>
								</Col>
							)}
							{features.comments && (
								<Col
									span={
										features.subtasks && !isMobile ? 12 : 24
									}
									className={
										!_itm.comments.list.length
											? 'no-print'
											: 'print-only-width-full'
									}
								>
									<Title level={5}>
										{translate('_comment.multiple')}
									</Title>
									<Form.Item
										label={t('_comment.multiple')}
										name="comments"
										tooltip={{
											title: t(
												'__add-allowed%_comment.multiple%_sprint'
											),
											icon: <InfoCircleOutlined />
										}}
									>
										<CommentsInput
											createdBy={loggedInUserId}
											disabled={
												!selfPermission &&
												!users.canUser(
													'write',
													tableEntity
												)
											}
										/>
									</Form.Item>
								</Col>
							)}
						</Row>
						{/* {!viewOnly && ( */}
						<Row
							gutter={[10, 10]}
							align="bottom"
							className="no-print"
						>
							<Col>
								<SubmitButton
									disabled={
										!selfPermission &&
										!users.canUser('write', tableEntity)
									}
								>
									{_itm.loaded === IS_NEW
										? t('single.create')
										: t('single.update')}
								</SubmitButton>
							</Col>
							{
								// Show  cancel button only if editing resource
								!viewOnly && (
									<Col>
										<Button
											type="default"
											onClick={() => navigate(-1)}
										>
											{t('single.cancel')}
										</Button>
									</Col>
								)
							}
							{viewOnly && (
								<Col>
									<Button
										type="default"
										onClick={() => window.print()}
									>
										{t('__download%single.pdf')}
									</Button>
								</Col>
							)}
							{!viewOnly && (
								<Col
									className="no-print"
									style={{
										marginLeft: 'auto'
									}}
								>
									<Space style={{ width: '100%' }}>
										<ImportExport
											form={form}
											withValuesExecute={
												_withValuesExecute
											}
										/>
									</Space>
								</Col>
							)}
						</Row>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}
SprintEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(SprintEdit)
